/**
 * Coordinates save-before-navigation for editors belonging to one clinical note.
 *
 * Each editor registers an adapter implementing:
 *   flush(): Promise<void>   - immediately save pending edits AND await any in-flight save
 *   getState(): { dirty: boolean, saving?: boolean, invalid?: boolean, error?: string }
 *
 * The coordinator does not sign, review, complete, or modify clinical rules.
 * An unconfirmed treatment preview is not dirty unless the clinician edits it.
 * Section adapters must keep their dirty/error state current in refs, not only
 * in React state, so getState() is accurate immediately after flush().
 */
export class ClinicalNoteNavigationCoordinator {
  constructor({ waitForPendingMutations } = {}) {
    this.editors = new Map();
    this.waitForPendingMutations = waitForPendingMutations;
    this.pendingNavigation = null;
  }

  /** Register a mounted editor. Returns its unregistration callback. */
  register(noteId, sectionKey, adapter) {
    if (!noteId || !sectionKey || typeof adapter?.flush !== 'function' ||
        typeof adapter?.getState !== 'function') {
      throw new Error('A note ID, unique section key, flush(), and getState() are required.');
    }
    const id = String(noteId);
    const key = `${id}:${sectionKey}`;
    if (this.editors.has(key)) {
      throw new Error(`Editor already registered: ${key}`);
    }
    this.editors.set(key, { id, sectionKey, adapter });
    return () => {
      if (this.editors.get(key)?.adapter === adapter) this.editors.delete(key);
    };
  }

  /** Return false (with error detail) rather than risking unsaved documentation. */
  async prepareToLeave(noteId) {
    if (this.pendingNavigation) return this.pendingNavigation;
    const job = this._prepareToLeave(noteId);
    this.pendingNavigation = job;
    try {
      return await job;
    } finally {
      if (this.pendingNavigation === job) this.pendingNavigation = null;
    }
  }

  async _prepareToLeave(noteId) {
    const id = String(noteId);
    const editors = [...this.editors.values()].filter(entry => entry.id === id);
    // Sequential flush prevents simultaneous editor flushes from racing.
    // Each editor's own service mutation must also use the per-note queue.
    for (const { sectionKey, adapter } of editors) {
      try {
        await adapter.flush();
      } catch (error) {
        return { ok: false, section: sectionKey,
          message: error?.message || 'A clinical section could not be saved.' };
      }
    }
    if (typeof this.waitForPendingMutations === 'function') {
      try {
        await this.waitForPendingMutations(noteId);
      } catch (error) {
        return { ok: false, message: error?.message || 'An outstanding save failed.' };
      }
    }
    for (const { sectionKey, adapter } of editors) {
      const state = adapter.getState() || {};
      if (state.error || state.invalid || state.dirty || state.saving) {
        return { ok: false, section: sectionKey,
          message: state.error || (state.invalid
            ? 'Correct the invalid unsaved values before switching visits.'
            : 'Changes are still unsaved. Please retry the save.') };
      }
    }
    return { ok: true };
  }
}

export default ClinicalNoteNavigationCoordinator;
